import { useContext } from 'react';
import {
    ActionDependant,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';

const StarIcon = ({ filled, size }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        className="cursor-pointer hover:scale-110 transition-transform"
    >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const WrenchIcon = ({ filled, size }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        className="cursor-pointer hover:scale-110 transition-transform"
    >
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
);

/**
 * StarRatingField — reactive-json form element for star/icon ratings.
 *
 * Props:
 *   dataLocation  – standard reactive-json form binding
 *   size          – icon size in px (default: 22)
 *   color         – Tailwind text color class (default: "text-yellow-400")
 *   starShape     – "star" (default) | "wrench" (for complexity)
 *   readOnly      – disables interaction (default: false)
 */
export const StarRatingField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: 0,
        globalDataContext,
        templateContext,
    });

    const size = props.size ?? 22;
    const color = props.color ?? 'text-yellow-400';
    const starShape = props.starShape ?? 'star';
    const readOnly = props.readOnly ?? false;

    const rating = Number(formData) || 0;

    const handleRate = (value) => {
        if (readOnly) return;
        globalDataContext.updateData(value, formDataPath);
    };

    return (
        <ActionDependant {...props}>
            <div
                className={`flex ${color} gap-0.5`}
                onPointerDown={(e) => e.stopPropagation()}
            >
                {[1, 2, 3, 4, 5].map((n) => (
                    <span
                        key={n}
                        onClick={() => handleRate(n)}
                    >
                        {starShape === 'wrench' ? (
                            <WrenchIcon filled={n <= rating} size={size} />
                        ) : (
                            <StarIcon filled={n <= rating} size={size} />
                        )}
                    </span>
                ))}
            </div>
        </ActionDependant>
    );
};
